/* Cart pack advice (snippets/st-pack-advice.liquid) and the pack ladder (snippets/st-cart-ladder.liquid,
   data-st-kind="ladder"): both switch a cart line to another pack. The switch adds the pack first and
   only then removes the old line: if the add fails, nothing has changed; if the remove fails, the
   shopper sees both lines and can remove one.
   On the cart page, success reloads the page so every total and Horizon's own cart state come from
   the server, and the confirmation is shown and focused after the reload.
   In the cart drawer a reload would close it, so the remove asks for the cart sections in the same
   request and announces a CartLinesUpdateEvent, the event Horizon's own quantity buttons use: the
   drawer redraws itself from those sections and the confirmation is shown and focused in it. */
if (!customElements.get('st-pack-advice')) {
  const KEY = 'st-pack-advice-done';
  customElements.define(
    'st-pack-advice',
    class extends HTMLElement {
      // Each kind keeps its own message across the reload, so the one that switched shows it.
      get #key() {
        return `${KEY}:${this.dataset.stKind || 'advice'}`;
      }

      connectedCallback() {
        const status = this.querySelector('[data-st-status]');
        let done = null;
        try {
          done = sessionStorage.getItem(this.#key);
          sessionStorage.removeItem(this.#key);
        } catch (e) {}
        if (done && status) {
          status.textContent = done;
          setTimeout(() => status.focus(), 0);
        }
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-st-switch]');
          if (button) this.#switch(button);
        });
      }

      /** Let Horizon redraw the drawer (and any other cart) from the sections the change returned. */
      async #redraw(line, changed) {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'update',
            context: 'cart',
            lines: [{ id: line.key, quantity: 0 }],
            promise: deferred.promise,
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(changed),
          detail: { sections: changed.sections, items: changed.items, itemCount: changed.item_count, source: 'st-pack-advice', didError: false },
        });
        // After the redraw the button is gone: say what changed and move focus to that message. The
        // redraw finishes on its own schedule (slower on a phone: a fixed 400ms wait focused the old
        // message, which the redraw then replaced), so wait until this line's button has gone.
        const drawer = () => [...document.querySelectorAll('dialog[open]')].find((d) => d.querySelector('cart-drawer-component'));
        const stillThere = () => drawer()?.querySelector(`[data-st-switch][data-key="${CSS.escape(line.key)}"]`);
        for (let waited = 0; stillThere() && waited < 3000; waited += 100) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        // The status line is display:none while empty, so find it by the open drawer, not by visibility.
        const status = drawer()?.querySelector(`st-pack-advice[data-st-kind="${this.dataset.stKind || 'advice'}"] [data-st-status]`);
        if (status) {
          status.textContent = line.done || '';
          status.focus();
        }
      }

      async #switch(button) {
        // Read the button once: the drawer's redraw can reuse this element for the next offer (the
        // ladder's button stays, pointing at the next pack), which would change these under us.
        const line = { key: button.dataset.key, to: button.dataset.to, done: button.dataset.done, preProp: button.dataset.preProp, preValue: button.dataset.preValue };
        const error = this.querySelector('[data-st-error]');
        const partial = this.querySelector('[data-st-partial]');
        if (error) error.hidden = true;
        if (partial) partial.hidden = true;
        let added = false;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const post = (url, body) =>
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          }).then((r) => {
            if (!r.ok) throw new Error(String(r.status));
            return r.json();
          });
        try {
          // A preorder pack (snippets/st-preorder.liquid) carries the Preorder property, as every other add does.
          const properties = line.preProp ? { properties: { [line.preProp]: line.preValue || '' } } : {};
          await post(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { items: [{ id: Number(line.to), quantity: 1, ...properties }] });
          added = true;
          const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((c) => c.dataset.sectionId);
          const changed = await post(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
            id: line.key,
            quantity: 0,
            ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
          });
          if (this.closest('cart-drawer-component') && changed.sections) {
            await this.#redraw(line, changed);
            return;
          }
          try {
            sessionStorage.setItem(this.#key, line.done || '');
          } catch (e) {}
          window.location.reload();
        } catch (e) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          // After a successful add the cart has changed, so say so rather than "nothing changed".
          const message = added ? partial : error;
          if (message) message.hidden = false;
          if (added) window.setTimeout(() => window.location.reload(), 4000);
        }
      }
    }
  );
}
